import { VirtualCardTypes } from 'src/enums'
import {
  CreditCardDisplayStatus,
  CreditCardStatus,
  CreditCardType,
} from 'src/models/creditCard.models'
import { CreditCardPermissions } from 'src/models/creditCardPermissions.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { VirtualCardPermissions } from 'src/models/virtualCardPermissions.models'
import {
  StatisticAggregateType,
  StatisticModelType,
  StatisticRequest,
} from 'src/requests/statistics.request'

export enum StatisticsKeys {
  CreditCardsSourcePhysicalClearingsLinkedCount = 'CreditCardsSourcePhysicalClearingsLinkedCount',
  VirtualCardsActiveCount = 'VirtualCardsActiveCount',
  VirtualCardsCount = 'VirtualCardsCount',
  BudgetsCount = 'BudgetsCount',
  CreditCardsActiveCanIssueVirtualCardsCount = 'CreditCardsActiveCanIssueVirtualCardsCount',
  CreditCardsActiveStandardCanIssueVirtualCardsCount = 'CreditCardsActiveStandardCanIssueVirtualCardsCount',
  CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount = 'CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount',
  CreditCardsActiveDirectEnabledCanIssueVirtualCardsCount = 'CreditCardsActiveDirectEnabledCanIssueVirtualCardsCount',
  CreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCount = 'CreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCount',
  CreditCardsActiveIssuerCycleEnabledCanIssueVirtualCardsCount = 'CreditCardsActiveIssuerCycleEnabledCanIssueVirtualCardsCount',
  CreditCardsActiveSourceCanIssueVirtualCardsCount = 'CreditCardsActiveSourceCanIssueVirtualCardsCount',
  CreditCardsActiveSourceStandardCanIssueVirtualCardsCount = 'CreditCardsActiveSourceStandardCanIssueVirtualCardsCount',
  CreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCount = 'CreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCount',
  CreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCount = 'CreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCount',
  CreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCount = 'CreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCount',
  CreditCardsActiveSourceIssuerCycleEnabledCanIssueVirtualCardsCount = 'CreditCardsActiveSourceIssuerCycleEnabledCanIssueVirtualCardsCount',
  VirtualCardsRecipientMeCount = 'VirtualCardsRecipientMeCount',
  CreditCardsDelegateCount = 'CreditCardsDelegateCount',
  CreditCardsActiveDelegateCount = 'CreditCardsActiveDelegateCount',
  CreditCardsSourceCount = 'CreditCardsSourceCount',
  CreditCardsOrgSourceCount = 'CreditCardsOrgSourceCount',
  CreditCardsActiveSourceCount = 'CreditCardsActiveSourceCount',
  // Wave-3 (EX-39745): the remaining stats from the legacy dispatcher's
  // appStatisticsKeys, renamed `<Stat>CountStatistics` → `<Stat>Count`. Bodies
  // below are ported verbatim from the statistics.actions.ts thunks they
  // replace — including quirks the backend depends on — so the migrated
  // requests match the legacy wire shapes.
  CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount = 'CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount',
  VirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount = 'VirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount',
  VirtualCardsCardholderOrViewerMeActiveAndInactiveCount = 'VirtualCardsCardholderOrViewerMeActiveAndInactiveCount',
  BillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCount = 'BillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCount',
  BillPayCardsCardholderOrViewerMeActiveAndInactiveCount = 'BillPayCardsCardholderOrViewerMeActiveAndInactiveCount',
  BillPayCardsPendingRequestsCount = 'BillPayCardsPendingRequestsCount',
  CreditCardsCanIssueVirtualCardsCount = 'CreditCardsCanIssueVirtualCardsCount',
  CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount = 'CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount',
  CreditCardsSourceBudgetsEnabledCount = 'CreditCardsSourceBudgetsEnabledCount',
  CreditCardsUserOrViewerMeCount = 'CreditCardsUserOrViewerMeCount',
  VirtualCardsPendingRequestCardholderMeCount = 'VirtualCardsPendingRequestCardholderMeCount',
  CreditCardsPendingSourceUserOrViewerMeCount = 'CreditCardsPendingSourceUserOrViewerMeCount',
  CreditCardsPendingSourceUserMeCount = 'CreditCardsPendingSourceUserMeCount',
  VirtualCardsCardholderPendingRequestOrApproverRuleApprovalCount = 'VirtualCardsCardholderPendingRequestOrApproverRuleApprovalCount',
  GuestVirtualCardsRecipientMeCount = 'GuestVirtualCardsRecipientMeCount',
  VirtualCardsInactiveRecipientMeIssuedCount = 'VirtualCardsInactiveRecipientMeIssuedCount',
  VirtualCardsActiveOrPendingRecipientMeCount = 'VirtualCardsActiveOrPendingRecipientMeCount',
  VirtualCardsActiveRecipientMeCount = 'VirtualCardsActiveRecipientMeCount',
  CreditCardsTimedOutSourceCount = 'CreditCardsTimedOutSourceCount',
  CreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCount = 'CreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCount',
  CreditCardsDelegateOwnerMeCount = 'CreditCardsDelegateOwnerMeCount',
  CreditCardsDelegateUserMeCount = 'CreditCardsDelegateUserMeCount',
  CreditCardsSourceUserOrViewerCount = 'CreditCardsSourceUserOrViewerCount',
  CreditCardsCount = 'CreditCardsCount',
}

export const StatisticsRequestsBody: Record<StatisticsKeys, StatisticRequest> =
  {
    [StatisticsKeys.CreditCardsSourcePhysicalClearingsLinkedCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        type: CreditCardType.Source,
        physicalClearingsEnabled: true,
        withPermission: CreditCardPermissions.Read,
      },
    },
    [StatisticsKeys.VirtualCardsActiveCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        status: VirtualCardStatus.Active,
      },
    },
    [StatisticsKeys.VirtualCardsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {},
    },
    [StatisticsKeys.BudgetsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {},
    },
    [StatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        status: CreditCardStatus.Active,
        withPermission: CreditCardPermissions.IssueVirtualCards,
      },
    },
    [StatisticsKeys.CreditCardsActiveStandardCanIssueVirtualCardsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        status: CreditCardStatus.Active,
        withPermission: CreditCardPermissions.IssueVirtualCards,
        balanceLockedEnabled: false,
      },
    },
    [StatisticsKeys.CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.CreditCard,
        query: {
          status: CreditCardStatus.Active,
          withPermission: CreditCardPermissions.IssueVirtualCards,
          recurrenceEnabled: true,
        },
      },
    [StatisticsKeys.CreditCardsActiveDirectEnabledCanIssueVirtualCardsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        status: CreditCardStatus.Active,
        withPermission: CreditCardPermissions.IssueVirtualCards,
        directEnabled: true,
      },
    },
    [StatisticsKeys.CreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.CreditCard,
        query: {
          status: CreditCardStatus.Active,
          withPermission: CreditCardPermissions.IssueVirtualCards,
          balanceLockedEnabled: true,
        },
      },
    [StatisticsKeys.CreditCardsActiveIssuerCycleEnabledCanIssueVirtualCardsCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.CreditCard,
        query: {
          status: CreditCardStatus.Active,
          withPermission: CreditCardPermissions.IssueVirtualCards,
          issuerCycleEnabled: true,
        },
      },
    [StatisticsKeys.CreditCardsActiveSourceCanIssueVirtualCardsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        status: CreditCardStatus.Active,
        type: CreditCardType.Source,
        withPermission: CreditCardPermissions.IssueVirtualCards,
      },
    },
    [StatisticsKeys.CreditCardsActiveSourceStandardCanIssueVirtualCardsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        status: CreditCardStatus.Active,
        type: CreditCardType.Source,
        withPermission: CreditCardPermissions.IssueVirtualCards,
        balanceLockedEnabled: false,
      },
    },
    [StatisticsKeys.CreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.CreditCard,
        query: {
          status: CreditCardStatus.Active,
          type: CreditCardType.Source,
          withPermission: CreditCardPermissions.IssueVirtualCards,
          recurrenceEnabled: true,
        },
      },
    [StatisticsKeys.CreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.CreditCard,
        query: {
          status: CreditCardStatus.Active,
          type: CreditCardType.Source,
          withPermission: CreditCardPermissions.IssueVirtualCards,
          directEnabled: true,
        },
      },
    [StatisticsKeys.CreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.CreditCard,
        query: {
          status: CreditCardStatus.Active,
          type: CreditCardType.Source,
          withPermission: CreditCardPermissions.IssueVirtualCards,
          balanceLockedEnabled: true,
        },
      },
    [StatisticsKeys.CreditCardsActiveSourceIssuerCycleEnabledCanIssueVirtualCardsCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.CreditCard,
        query: {
          status: CreditCardStatus.Active,
          type: CreditCardType.Source,
          withPermission: CreditCardPermissions.IssueVirtualCards,
          issuerCycleEnabled: true,
        },
      },
    [StatisticsKeys.VirtualCardsRecipientMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        recipient: 'me',
      },
    },
    [StatisticsKeys.CreditCardsDelegateCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        type: CreditCardType.Delegate,
      },
    },
    [StatisticsKeys.CreditCardsActiveDelegateCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        status: CreditCardStatus.Active,
        type: CreditCardType.Delegate,
      },
    },
    [StatisticsKeys.CreditCardsSourceCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        type: CreditCardType.Source,
      },
    },
    // Org-wide source-card count. The viewer-scoped `CreditCardsSourceCount`
    // above counts only cards the authed user can read, which would miss
    // cards registered by other members of the org (e.g., a previous owner
    // who got demoted). EX-39432 uses this for the onboarding drop-off
    // redirect so a promoted owner inheriting an already-set-up org doesn't
    // get pushed back through card registration.
    [StatisticsKeys.CreditCardsOrgSourceCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        type: CreditCardType.Source,
        all: true,
      },
    },
    [StatisticsKeys.CreditCardsActiveSourceCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        displayStatuses: [CreditCardDisplayStatus.Active],
        type: CreditCardType.Source,
      },
    },
    [StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.CreditCard,
        query: {
          type: CreditCardType.Source,
          status: CreditCardStatus.Active,
          budgetsEnabled: true,
          withPermission: CreditCardPermissions.IssueDelegateCreditCards,
        },
      },
    [StatisticsKeys.VirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.VirtualCard,
        query: {
          status: VirtualCardStatus.Active,
          cardholderOrViewer: 'me',
        },
      },
    [StatisticsKeys.VirtualCardsCardholderOrViewerMeActiveAndInactiveCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        issued: true,
        cardholderOrViewer: 'me',
        statuses: [
          VirtualCardStatus.Active,
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Consumed,
          VirtualCardStatus.Closed,
          VirtualCardStatus.Expired,
        ],
      },
    },
    [StatisticsKeys.BillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.VirtualCard,
        query: {
          status: VirtualCardStatus.Active,
          cardholderOrViewer: 'me',
          cardTypes: [VirtualCardTypes.billPay],
        },
      },
    [StatisticsKeys.BillPayCardsCardholderOrViewerMeActiveAndInactiveCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        issued: true,
        cardholderOrViewer: 'me',
        statuses: [
          VirtualCardStatus.Active,
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Consumed,
          VirtualCardStatus.Closed,
          VirtualCardStatus.Expired,
        ],
        cardTypes: [VirtualCardTypes.billPay],
      },
    },
    [StatisticsKeys.BillPayCardsPendingRequestsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        cardholderPendingRequestOrApproverRuleApproval: 'me',
        withPermission: VirtualCardPermissions.UpdateVirtualCard,
        cardTypes: [VirtualCardTypes.billPay],
      },
    },
    [StatisticsKeys.CreditCardsCanIssueVirtualCardsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        withPermission: CreditCardPermissions.IssueVirtualCards,
      },
    },
    [StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        type: CreditCardType.Source,
        budgetsEnabled: true,
        withPermission: CreditCardPermissions.IssueDelegateCreditCards,
      },
    },
    [StatisticsKeys.CreditCardsSourceBudgetsEnabledCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        type: CreditCardType.Source,
        budgetsEnabled: true,
      },
    },
    // The legacy thunk pads this query with throwaway fields because the
    // backend once rejected empty query objects; kept verbatim.
    [StatisticsKeys.CreditCardsUserOrViewerMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        neverGonnaGive: 'youUp',
        neverGonnaLet: 'youDown',
      },
    },
    [StatisticsKeys.VirtualCardsPendingRequestCardholderMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        pendingRequest: true,
        cardholder: 'me',
      },
    },
    // The legacy thunks send the deprecated singular `displayStatus`; the
    // backend coalesces `displayStatuses ?: displayStatus`, so the typed
    // plural form is wire-equivalent (same normalization as
    // CreditCardsActiveSourceCount above).
    [StatisticsKeys.CreditCardsPendingSourceUserOrViewerMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        displayStatuses: [CreditCardDisplayStatus.Pending],
        type: CreditCardType.Source,
      },
    },
    [StatisticsKeys.CreditCardsPendingSourceUserMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        displayStatuses: [CreditCardDisplayStatus.Pending],
        type: CreditCardType.Source,
        user: 'me',
      },
    },
    [StatisticsKeys.VirtualCardsCardholderPendingRequestOrApproverRuleApprovalCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.VirtualCard,
        query: {
          cardholderPendingRequestOrApproverRuleApproval: 'me',
          withPermission: VirtualCardPermissions.UpdateVirtualCard,
        },
      },
    [StatisticsKeys.GuestVirtualCardsRecipientMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        recipient: 'me',
        isDirect: true,
      },
    },
    [StatisticsKeys.VirtualCardsInactiveRecipientMeIssuedCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        statuses: [
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Consumed,
          VirtualCardStatus.Closed,
          VirtualCardStatus.Expired,
        ],
        recipient: 'me',
        issued: true,
      },
    },
    [StatisticsKeys.VirtualCardsActiveOrPendingRecipientMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        statuses: [VirtualCardStatus.Active, VirtualCardStatus.Pending],
        recipient: 'me',
        withPermission: VirtualCardPermissions.RequestUpdate,
      },
    },
    // `withpermission` (lowercase p) is a preserved legacy typo — see the
    // field's note in GetVirtualCardsRequest. Kept verbatim so the request
    // matches the legacy thunk's wire shape.
    [StatisticsKeys.VirtualCardsActiveRecipientMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.VirtualCard,
      query: {
        statuses: [VirtualCardStatus.Active],
        recipient: 'me',
        withpermission: VirtualCardPermissions.RequestUpdate,
      },
    },
    [StatisticsKeys.CreditCardsTimedOutSourceCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        status: CreditCardStatus.Inactive,
        type: CreditCardType.Source,
      },
    },
    [StatisticsKeys.CreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCount]:
      {
        agg: StatisticAggregateType.Count,
        type: StatisticModelType.CreditCard,
        query: {
          type: CreditCardType.Source,
          status: CreditCardStatus.Pending,
          needsCardholderVerification: true,
        },
      },
    [StatisticsKeys.CreditCardsDelegateOwnerMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        type: CreditCardType.Delegate,
        owner: 'me',
      },
    },
    // Body from the legacy getBudgetsUserMeCountStatistics thunk: despite its
    // name, that thunk is what the dispatcher fired for the DelegateUserMe key
    // and its success action wrote creditCardsDelegateUserMeCount.
    [StatisticsKeys.CreditCardsDelegateUserMeCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        type: CreditCardType.Delegate,
        status: CreditCardStatus.Active,
        user: 'me',
      },
    },
    [StatisticsKeys.CreditCardsSourceUserOrViewerCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        type: CreditCardType.Source,
      },
    },
    // Same empty-query padding workaround as CreditCardsUserOrViewerMeCount.
    [StatisticsKeys.CreditCardsCount]: {
      agg: StatisticAggregateType.Count,
      type: StatisticModelType.CreditCard,
      query: {
        neverGonnaGive: 'youUp',
        neverGonnaLet: 'youDown',
      },
    },
  }
